import { useCallback, useEffect, useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import { handlePromise } from "../../../utils/handlePromisse";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";
import { UserModel } from "./table/user.model";
import { getPaginatedUsers, UserFilter } from "../../../services/userService";
import { FilterType, PlanFilterType } from "./form/user-form";
import { deleteUser, inviteUser, InviteUserRequest } from "../../../services/adm";
import { getPlans } from "../../../services/plan-service";
import PlanModel from "../../../models/plan.model";
import { getAdminUsersNotRenewed } from "../../../services/admin-stats.service";
import { downloadCsv } from "../../../utils/export-csv";

export type InviteForm = {
    name: string;
    email: string;
    phone: string;
    plan: string;
    role: "USER" | "ADMIN";
    active: boolean;
};

const emptyInvite: InviteForm = {
    name: "",
    email: "",
    phone: "",
    plan: "",
    role: "USER",
    active: true,
};

/** Estado, fetch e handlers da página de usuários (listagem + convite + cancelar plano). */
export function useUsersPage() {
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<UserFilter>({ page: 1, size: 10 });

    const [openDialog, setOpenDialog] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [userId, setUserId] = useState<number | undefined>(undefined);
    const [users, setUsers] = useState<PaginatedResponse<UserModel>>(initialPagination);

    // filter bar local state
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<FilterType>("name");
    const [planFilter, setPlanFilter] = useState<PlanFilterType>("all");

    // invite modal
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteForm, setInviteForm] = useState<InviteForm>(emptyInvite);
    const [inviteSaving, setInviteSaving] = useState(false);
    const [plans, setPlans] = useState<PlanModel[]>([]);

    const setInviteField = <K extends keyof InviteForm>(key: K, value: InviteForm[K]) =>
        setInviteForm((prev) => ({ ...prev, [key]: value }));

    const isInviteValid = inviteForm.name.trim().length > 0 && /\S+@\S+\.\S+/.test(inviteForm.email);

    const fetchPlans = useCallback(async () => {
        const [response, error] = await handlePromise(getPlans());
        if (!error) setPlans(response.data as PlanModel[]);
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        const [result, error] = await handlePromise(getPaginatedUsers(filter));
        setLoading(false);

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }

        setUsers(result.data as PaginatedResponse<UserModel>);
    }, [filter]);

    const openInvite = useCallback(() => {
        setInviteForm(emptyInvite);
        setInviteOpen(true);
    }, []);

    const submitInvite = useCallback(async () => {
        setInviteSaving(true);
        const payload: InviteUserRequest = {
            name: inviteForm.name.trim(),
            email: inviteForm.email.trim(),
            phone: inviteForm.phone.trim() || undefined,
            plan: inviteForm.plan ? Number(inviteForm.plan) : undefined,
            role: inviteForm.role,
            active: inviteForm.active,
        };
        const [, error] = await handlePromise(inviteUser(payload));
        setInviteSaving(false);

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(
                error.response?.status === 404
                    ? "Convite de usuário ainda não disponível no servidor."
                    : error.response?.data?.message || "Não foi possível convidar o usuário."
            );
            return;
        }

        setInviteOpen(false);
        setShowSnackbar(true);
        setSnackbarMessage("Usuário convidado com sucesso!");
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteForm]);

    const cancelPlan = useCallback(async () => {
        setLoading(true);

        const [_, error] = await handlePromise(deleteUser(userId || -1));
        setLoading(false);

        if (!!error) {
            setShowSnackbar(true);
            setSnackbarMessage(error.response.data.message);
            return;
        }

        setOpenDialog(false);
        setShowSnackbar(true);
        setSnackbarMessage("Plano cancelado com sucesso!");

        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handlePageChange = useCallback((newPage: number) => {
        setFilter((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handlePageSizeChange = useCallback((newSize: number) => {
        setFilter((prev) => ({ ...prev, size: newSize, page: 1 }));
    }, []);

    const handleCancelPlan = useCallback((id: number) => {
        setUserId(id);
        setOpenDialog(true);
    }, []);

    const [exporting, setExporting] = useState(false);

    const handleExportNotRenewed = useCallback(async () => {
        setExporting(true);
        const [result, error] = await handlePromise(getAdminUsersNotRenewed());
        setExporting(false);
        if (error) {
            setShowSnackbar(true);
            setSnackbarMessage("Erro ao exportar a lista.");
            return;
        }
        const list = result.data;
        if (!list || list.length === 0) {
            setShowSnackbar(true);
            setSnackbarMessage("Nenhuma usuária não renovada no período.");
            return;
        }
        downloadCsv(
            `usuarias-nao-renovadas-${new Date().toISOString().slice(0, 10)}.csv`,
            ["Nome", "E-mail", "Telefone", "Plano", "Expirou em"],
            list.map((u) => [u.name, u.email, u.phone, u.plan, u.expiredAt])
        );
    }, []);

    const applyFilter = useCallback((newType: FilterType, newPlan: PlanFilterType, newQuery: string) => {
        let withPlan: undefined | boolean;
        if (newPlan === "yes") withPlan = true;
        else if (newPlan === "no") withPlan = false;
        else withPlan = undefined;

        setFilter((prev) => ({
            ...prev,
            page: 1,
            type: newType,
            query: newQuery,
            withPlan,
        }));
    }, []);

    const onQueryChange = (value: string) => {
        setQuery(value);
        applyFilter(typeFilter, planFilter, value);
    };

    const onTypeChange = (event: SelectChangeEvent) => {
        const t = event.target.value as FilterType;
        setTypeFilter(t);
        applyFilter(t, planFilter, query);
    };

    const onPlanChange = (event: SelectChangeEvent) => {
        const p = event.target.value as PlanFilterType;
        setPlanFilter(p);
        applyFilter(typeFilter, p, query);
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return {
        loading,
        openDialog,
        setOpenDialog,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        userId,
        users,
        query,
        typeFilter,
        planFilter,
        inviteOpen,
        setInviteOpen,
        inviteForm,
        setInviteField,
        inviteSaving,
        plans,
        isInviteValid,
        openInvite,
        submitInvite,
        cancelPlan,
        exporting,
        handleExportNotRenewed,
        handlePageChange,
        handlePageSizeChange,
        handleCancelPlan,
        onQueryChange,
        onTypeChange,
        onPlanChange,
    };
}
